import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Scale,
  ShieldCheck,
  BookOpen,
  Percent,
  Layers,
  AlertCircle,
  Landmark,
  UserCheck,
  Gift,
  ArrowRight,
  Calculator,
} from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import Callout from "../components/Callout";
import { SITE_NAME } from "../config/site";

const ENTRY_CARDS = [
  {
    icon: GraduationCap,
    title: "Je débute",
    text: "Comprendre ce qu'est un pari, une cote et un combiné.",
    to: "/comprendre/quest-ce-quun-pari-sportif",
    testid: "entry-debute",
  },
  {
    icon: Scale,
    title: "Je veux comprendre les règles",
    text: "Cadre légal français et opérateurs agréés.",
    to: "/cadre-legal/site-legal-ou-illegal",
    testid: "entry-regles",
  },
  {
    icon: ShieldCheck,
    title: "Je veux jouer en sécurité",
    text: "Risques, limites et jeu responsable.",
    to: "/jeu-responsable",
    testid: "entry-securite",
  },
];

const FIVE_THINGS = [
  "Il faut avoir 18 ans révolus.",
  "Seuls les opérateurs agréés par l'Autorité nationale des jeux (ANJ) sont légaux en France.",
  "Les cotes intègrent une marge de l'opérateur : sur la durée, le joueur est statistiquement perdant.",
  "Un bonus de bienvenue n'est jamais « gratuit » : il s'accompagne de conditions.",
  "On ne mise que ce qu'on peut se permettre de perdre, et des outils de limitation existent.",
];

const GUIDES = [
  {
    icon: Percent,
    title: "Les cotes expliquées simplement",
    text: "Gain total, gain net, probabilité implicite et marge de l'opérateur.",
    to: "/comprendre/les-cotes",
  },
  {
    icon: Layers,
    title: "Les types de paris (simple, combiné, live...)",
    text: "Principe, exemple chiffré et vigilance pour chaque format.",
    to: "/comprendre/les-types-de-paris",
  },
  {
    icon: AlertCircle,
    title: "Les erreurs classiques des débutants",
    text: "Courir après ses pertes, croire aux tuyaux, et autres pièges.",
    to: "/comprendre/erreurs-et-mythes",
  },
  {
    icon: Landmark,
    title: "L'ANJ : qui contrôle les paris en France ?",
    text: "Rôle et missions du régulateur français des jeux d'argent.",
    to: "/cadre-legal/anj",
  },
  {
    icon: UserCheck,
    title: "S'inscrire chez un opérateur : les étapes",
    text: "Compte, limites, vérification d'identité : le guide pas à pas.",
    to: "/demarrer/sinscrire-pas-a-pas",
  },
  {
    icon: Gift,
    title: "Bonus de bienvenue : comment ça marche vraiment",
    text: "Freebet, rollover, cote minimale : décrypter les conditions.",
    to: "/demarrer/bonus-de-bienvenue",
  },
];

const TOOLS = [
  {
    title: "Calculateur de cotes",
    text: "Gain total, gain net et probabilité implicite.",
    to: "/comprendre/les-cotes#calculateur",
  },
  {
    title: "Simulateur de combiné",
    text: "Voyez la probabilité diminuer à chaque sélection ajoutée.",
    to: "/combines#simulateur-combine",
  },
  {
    title: "Simulateur de bonus",
    text: "Estimez le montant à miser avant de pouvoir retirer.",
    to: "/demarrer/bonus-de-bienvenue#simulateur-bonus",
  },
];

const MARQUEE = [
  "Site 100 % pédagogique",
  "Aucune affiliation",
  "Aucun pronostic",
  "Aucune promesse de gain",
  "Comprendre avant de jouer",
  "Réservé aux majeurs",
];

export default function Home() {
  const reduce = useReducedMotion();

  const line = (text, delay) => (
    <span className="mask-line">
      <motion.span
        initial={reduce ? false : { y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {text}
      </motion.span>
    </span>
  );

  return (
    <>
      <Seo
        title={`${SITE_NAME} – Comprendre les paris sportifs en France`}
        description="Guide indépendant et pédagogique pour comprendre les paris sportifs : cotes, cadre légal, inscription, bonus de bienvenue et jeu responsable."
        path="/"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#F3F5F8]">
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full border-[3rem] border-[#2BB08A]/10"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-32 right-1/4 h-72 w-72 rounded-full bg-[#0B1F3A]/5"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="max-w-[760px]">
            <motion.p
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#2BB08A]"
              data-testid="hero-overline"
            >
              Guide indépendant — sans publicité ni lien commercial
            </motion.p>
            <h1
              data-testid="home-h1"
              className="font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#0B1F3A] sm:text-5xl lg:text-6xl"
            >
              {line("Paris sportifs :", 0.1)}
              {line("comprendre avant de jouer", 0.25)}
            </h1>
            <motion.p
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-6 text-lg leading-relaxed text-slate-600"
            >
              Cotes, règles, cadre légal, bonus de bienvenue, risques : {SITE_NAME} explique
              les paris sportifs en France simplement et sans détour. Un site indépendant, sans
              publicité ni lien commercial, pour les novices comme pour les curieux.
            </motion.p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ENTRY_CARDS.map((card, i) => (
              <Reveal key={card.title} delay={0.1 + i * 0.12}>
                <Link
                  to={card.to}
                  data-testid={card.testid}
                  className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                >
                  <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-[#E8F8F3] transition-colors group-hover:bg-[#2BB08A]">
                    <card.icon
                      className="h-5.5 w-5.5 h-6 w-6 text-[#2BB08A] transition-colors group-hover:text-white"
                      aria-hidden="true"
                    />
                  </span>
                  <h2 className="font-display text-lg font-semibold text-[#0B1F3A]">
                    {card.title}
                  </h2>
                  <p className="mt-1.5 flex-1 text-[15px] leading-relaxed text-slate-600">
                    {card.text}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2BB08A]">
                    Découvrir
                    <ArrowRight
                      className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee éditorial */}
      <div
        className="overflow-hidden border-y border-[#16305a] bg-[#0B1F3A] py-3"
        aria-hidden="true"
      >
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 items-center">
              {MARQUEE.map((m) => (
                <span
                  key={`${dup}-${m}`}
                  className="mx-6 flex items-center gap-6 whitespace-nowrap font-display text-sm font-medium tracking-wide text-white/70"
                >
                  {m}
                  <span className="h-1.5 w-1.5 rounded-full bg-[#2BB08A]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* 5 choses à savoir */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#2BB08A]">
            L'essentiel
          </p>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0B1F3A] sm:text-3xl">
            5 choses à savoir avant de parier
          </h2>
        </Reveal>
        <div className="mt-8 space-y-4">
          {FIVE_THINGS.map((thing, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div
                data-testid={`five-things-${i}`}
                className="flex items-start gap-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <span className="font-display text-2xl font-bold text-[#2BB08A]/40 sm:text-3xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="pt-1 text-[15px] leading-relaxed text-slate-700">{thing}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Guides essentiels */}
      <section className="bg-[#F3F5F8] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#2BB08A]">
              Par où commencer
            </p>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0B1F3A] sm:text-3xl">
              Les guides essentiels
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {GUIDES.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.06}>
                <Link
                  to={g.to}
                  data-testid={`guide-card-${i}`}
                  className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                >
                  <g.icon className="mb-3 h-6 w-6 text-[#2BB08A]" aria-hidden="true" />
                  <h3 className="font-display text-base font-semibold leading-snug text-[#0B1F3A] transition-colors group-hover:text-[#2BB08A]">
                    {g.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{g.text}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Outils */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#2BB08A]">
            Apprendre en manipulant
          </p>
          <h2 className="flex items-center gap-2.5 font-display text-2xl font-semibold tracking-tight text-[#0B1F3A] sm:text-3xl">
            <Calculator className="h-7 w-7 text-[#2BB08A]" aria-hidden="true" />
            Les outils pédagogiques
          </h2>
          <p className="mt-3 max-w-[760px] text-[15px] leading-relaxed text-slate-600">
            Des calculateurs gratuits, sans inscription et sans enregistrement de données, pour
            comprendre concrètement les mécanismes des paris.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {TOOLS.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.08}>
              <Link
                to={t.to}
                data-testid={`tool-card-${i}`}
                className="group block h-full rounded-xl border-2 border-[#0B1F3A] bg-white p-6 transition-colors hover:bg-[#0B1F3A]"
              >
                <h3 className="font-display text-base font-semibold text-[#0B1F3A] transition-colors group-hover:text-white">
                  {t.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600 transition-colors group-hover:text-white/75">
                  {t.text}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2BB08A]">
                  Utiliser l'outil
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pourquoi ce site */}
      <section className="bg-[#F3F5F8] py-16 sm:py-20">
        <div className="mx-auto max-w-[760px] px-4 sm:px-6">
          <Reveal>
            <h2 className="flex items-center gap-2.5 font-display text-2xl font-semibold tracking-tight text-[#0B1F3A] sm:text-3xl">
              <BookOpen className="h-7 w-7 text-[#2BB08A]" aria-hidden="true" />
              Pourquoi ce site ?
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate-700">
              Le marché français des paris sportifs est régulé, mais les informations pour les
              nouveaux joueurs sont souvent dispersées, techniques ou orientées commercialement.{" "}
              {SITE_NAME} a une seule ambition : donner à chacun les clés pour comprendre, décider
              en connaissance de cause, et rester maître de son jeu. Nous ne touchons aucune
              commission et ne vous incitons pas à parier.
            </p>
            <Callout kind="retain" title="Transparence">
              Ce site est indépendant et ne perçoit aucune rémunération des opérateurs. Il ne
              propose ni pari, ni pronostic, ni inscription.
            </Callout>
          </Reveal>
        </div>
      </section>
    </>
  );
}
